export default function createCommunityTwoPassTick(sim, communities, communityForceNames, nodeForceNames) {
  // Limit the magnitude of movement between ticks. Increased to allow faster
  // repositioning when strong forces are applied.
  const MAX_VELOCITY = 200;
  const centroidForce = sim.force('centroid');
  const communityForces = communityForceNames
    .filter((name) => name !== 'centroid')
    .map((name) => sim.force(name))
    .filter((f) => typeof f === 'function');
  const nodeForces = nodeForceNames
    .map((name) => sim.force(name))
    .filter((f) => typeof f === 'function');
  if (!centroidForce && communityForces.length === 0 && nodeForces.length === 0)
    return sim.tick;

  const velocityDecay = 1 - sim.velocityDecay();
  const alphaTarget = sim.alphaTarget();
  const alphaDecay = sim.alphaDecay();

  return function(iterations = 1) {
    for (let k = 0; k < iterations; ++k) {
      let alpha = sim.alpha();
      alpha += (alphaTarget - alpha) * alphaDecay;
      sim.alpha(alpha);

      // --- centroid pass ---
      if (centroidForce) {
        if (typeof centroidForce.initialize === 'function') centroidForce.initialize(sim.nodes());
        centroidForce(alpha);
      }

      const vnodes = communities.map((c) => c.vn);
      const prev = new Map();
      for (const vn of vnodes) {
        prev.set(vn, { x: vn.x || 0, y: vn.y || 0 });
      }

      // --- community pass ---
      for (const force of communityForces) {
        if (typeof force.initialize === 'function') force.initialize(vnodes);
        force(alpha);
      }

      // update virtual nodes and propagate movement to members
      for (const c of communities) {
        const vn = c.vn;
        const p = prev.get(vn) || { x: vn.x || 0, y: vn.y || 0 };
        let vx = (vn.vx || 0) * velocityDecay;
        let vy = (vn.vy || 0) * velocityDecay;
        const vmag = Math.sqrt(vx * vx + vy * vy);
        if (vmag > MAX_VELOCITY && vmag > 0) {
          const scale = MAX_VELOCITY / vmag;
          vx *= scale;
          vy *= scale;
        }
        if (vn.fx == null) {
          vn.x = (vn.x || 0) + vx;
          vn.vx = vx;
        } else {
          vn.x = vn.fx;
          vn.vx = 0;
        }
        if (vn.fy == null) {
          vn.y = (vn.y || 0) + vy;
          vn.vy = vy;
        } else {
          vn.y = vn.fy;
          vn.vy = 0;
        }
        const dx = (vn.x || 0) - p.x;
        const dy = (vn.y || 0) - p.y;
        if (dx || dy) {
          for (const n of c.nodes) {
            if (n.fx == null) n.x = (n.x || 0) + dx; else n.x = n.fx;
            if (n.fy == null) n.y = (n.y || 0) + dy; else n.y = n.fy;
          }
        }
      }

      // --- node pass ---
      for (const c of communities) {
        const nodes = c.nodes;
        if (!nodes || nodes.length === 0) continue;
        for (const force of nodeForces) {
          if (typeof force.links === 'function' && c.links) force.links(c.links);
          if (typeof force.initialize === 'function') force.initialize(nodes);
          force(alpha);
        }
        for (const n of nodes) {
          let vx = (n.vx || 0) * velocityDecay;
          let vy = (n.vy || 0) * velocityDecay;
          const vmag = Math.sqrt(vx * vx + vy * vy);
          if (vmag > MAX_VELOCITY && vmag > 0) {
            const scale = MAX_VELOCITY / vmag;
            vx *= scale;
            vy *= scale;
          }
          if (n.fx == null) {
            n.x = (n.x || 0) + vx;
            n.vx = vx;
          } else {
            n.x = n.fx;
            n.vx = 0;
          }
          if (n.fy == null) {
            n.y = (n.y || 0) + vy;
            n.vy = vy;
          } else {
            n.y = n.fy;
            n.vy = 0;
          }
        }
      }
    }
    return sim;
  };
}
